import type { z } from 'zod';
import { ApiError, prepareApiRequest, readApiResponse, type ApiClientOptions } from './client';

/**
 * Upload a file as the raw request body, with progress (T-0156).
 *
 * `fetch` cannot report how much of a request body has been sent, so the
 * uploads use `XMLHttpRequest` and its `upload.onprogress`. Everything
 * else is `apiClient`'s: the same URL and headers (`prepareApiRequest`:
 * bearer or viewer grant, `X-Correlation-Id`, `Idempotency-Key`), and the
 * same reading of the answer (`readApiResponse`: error envelope, 401
 * recovery, schema check). A failure is an `ApiError` exactly as
 * `apiClient` would throw it.
 *
 * Two phases are reported:
 *   - `onProgress({ loaded, total, fraction })` while bytes are sent.
 *     `total` is the file size when the browser cannot compute it.
 *   - `onUploaded()` once, when the last byte has been sent. The server is
 *     still working on it; the caller shows "Processing…" until the
 *     promise settles.
 *
 * Abort: aborting `signal` calls `xhr.abort()` and the promise rejects
 * with `ApiError(0, 'network')`, as an aborted `fetch` does through
 * `apiClient`. Its `cause` is a `DOMException` named `AbortError`
 * (`isAbortError`).
 */

export interface UploadProgress {
  /** Bytes sent so far. */
  loaded: number;
  /** Bytes to send. */
  total: number;
  /** `loaded / total`, between 0 and 1. */
  fraction: number;
}

export interface UploadProgressHandlers {
  /** Bytes sent of the total, as the browser reports them. */
  onProgress?: (progress: UploadProgress) => void;
  /** The last byte has been sent; the server has not answered yet. Called once. */
  onUploaded?: () => void;
}

export interface UploadOptions
  extends
    UploadProgressHandlers,
    Pick<ApiClientOptions, 'query' | 'headers' | 'idempotencyKey' | 'noAuth' | 'asViewer'> {
  method?: 'POST' | 'PUT' | 'PATCH';
  signal?: AbortSignal;
}

export const UPLOAD_ABORTED_MESSAGE = 'The upload was cancelled.';

function abortError(): DOMException {
  return new DOMException(UPLOAD_ABORTED_MESSAGE, 'AbortError');
}

function abortedApiError(correlationId: string): ApiError {
  const err = new ApiError(0, 'network', UPLOAD_ABORTED_MESSAGE, correlationId);
  err.cause = abortError();
  return err;
}

/** True for the rejection of an upload whose signal was aborted. */
export function isAbortError(err: unknown): boolean {
  if (err instanceof DOMException) return err.name === 'AbortError';
  return err instanceof ApiError && err.cause instanceof DOMException
    ? err.cause.name === 'AbortError'
    : false;
}

export function uploadProgressOf(loaded: number, total: number): UploadProgress {
  const safeTotal = Math.max(0, total);
  const safeLoaded = Math.min(Math.max(0, loaded), safeTotal);
  return {
    loaded: safeLoaded,
    total: safeTotal,
    fraction: safeTotal === 0 ? 1 : safeLoaded / safeTotal,
  };
}

/** Send `body` to `path` and parse the answer with `schema`. */
export async function uploadWithProgress<TResp>(
  path: string,
  body: Blob,
  schema: z.ZodType<TResp>,
  options: UploadOptions = {},
): Promise<TResp> {
  const { signal, onProgress, onUploaded } = options;
  const { url, headers, correlationId } = await prepareApiRequest(path, {
    query: options.query,
    headers: options.headers,
    idempotencyKey: options.idempotencyKey,
    noAuth: options.noAuth,
    asViewer: options.asViewer,
  });
  // Aborted while the token was being read: nothing was sent.
  if (signal?.aborted) throw abortedApiError(correlationId);

  const size = body.size;
  const answer = await new Promise<{ status: number; text: string }>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let uploaded = false;
    let settled = false;

    const markUploaded = () => {
      if (uploaded) return;
      uploaded = true;
      onUploaded?.();
    };
    const onAbort = () => xhr.abort();
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener('abort', onAbort);
      fn();
    };

    xhr.open(options.method ?? 'POST', url);
    for (const [name, value] of Object.entries(headers)) xhr.setRequestHeader(name, value);

    xhr.upload.onprogress = (e: ProgressEvent) => {
      if (settled) return;
      const total = e.lengthComputable && e.total > 0 ? e.total : size;
      const progress = uploadProgressOf(e.loaded, total);
      onProgress?.(progress);
      if (progress.total > 0 && progress.loaded >= progress.total) markUploaded();
    };
    xhr.upload.onload = () => {
      if (settled) return;
      onProgress?.(uploadProgressOf(size, size));
      markUploaded();
    };

    xhr.onload = () => finish(() => resolve({ status: xhr.status, text: xhr.responseText }));
    xhr.onerror = () =>
      finish(() => reject(new ApiError(0, 'network', 'Network error.', correlationId)));
    xhr.ontimeout = () =>
      finish(() => reject(new ApiError(0, 'network', 'The upload timed out.', correlationId)));
    xhr.onabort = () => finish(() => reject(abortedApiError(correlationId)));

    signal?.addEventListener('abort', onAbort, { once: true });
    xhr.send(body);
  });

  return readApiResponse(
    {
      status: answer.status,
      ok: answer.status >= 200 && answer.status < 300,
      json: () => Promise.resolve().then(() => JSON.parse(answer.text) as unknown),
    },
    schema,
    path,
    correlationId,
  );
}
